import { Button, Flex, Form, Input, Select, Space, Table, Tag, Typography, notification } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EditOutlined, ReloadOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useState } from 'react';
import { UpdateModule } from '../api/go';
import type { UpdateModuleStatus, UpdateModuleVersionRecord, UpdatePageResp, UpdateReleaseStatus } from '../api/go';
import {
  MODULE_STATUS_ENABLED,
  RELEASE_STATUS_BASELINE,
  RELEASE_STATUS_DRAFT,
  RELEASE_STATUS_ENABLED,
  canManageModuleRollout,
  getErrorMessage,
  groupModules,
  moduleStatusMap,
  moduleStatusOptions,
  releaseStatusOptions,
  renderOssKey,
  renderStatusTag,
} from './shared';
import { useRolloutEditor } from './rollout_editor';

type ModuleFilterForm = {
  module_key?: string;
  release_statuses?: UpdateReleaseStatus[];
  module_statuses?: UpdateModuleStatus[];
};

const defaultReleaseStatusFilter: UpdateReleaseStatus[] = [RELEASE_STATUS_DRAFT, RELEASE_STATUS_ENABLED, RELEASE_STATUS_BASELINE];
const defaultModuleStatusFilter: UpdateModuleStatus[] = [MODULE_STATUS_ENABLED];

function compareModuleVersionCreatedAtDesc(a: UpdateModuleVersionRecord, b: UpdateModuleVersionRecord) {
  const createdAtCompare = (b.created_at || '').localeCompare(a.created_at || '');
  if (createdAtCompare !== 0) {
    return createdAtCompare;
  }
  return b.id - a.id;
}

export function ModuleVersionTab(props: { refreshKey: number; onRolloutChanged: () => void }) {
  const { refreshKey, onRolloutChanged } = props;
  const [filterForm] = Form.useForm<ModuleFilterForm>();
  const [records, setRecords] = useState<UpdateModuleVersionRecord[]>([]);
  const [page, setPage] = useState({ current: 1, size: 100, total: 0 });
  const [loading, setLoading] = useState(false);
  const grouped = useMemo(() => groupModules([...records].sort(compareModuleVersionCreatedAtDesc)), [records]);
  const rolloutEditor = useRolloutEditor({
    onSaved: async () => {
      onRolloutChanged();
      await loadModuleList();
    },
  });

  async function loadModuleList(current = page.current, size = page.size) {
    setLoading(true);
    try {
      const values = filterForm.getFieldsValue();
      const data: UpdatePageResp<UpdateModuleVersionRecord> = await UpdateModule.versionList({
        module_key: values.module_key?.trim() || undefined,
        release_statuses: values.release_statuses,
        module_statuses: values.module_statuses,
        current,
        size,
      });
      setRecords(data.records || []);
      setPage({ current: data.current, size: data.size, total: data.total });
    } catch (err) {
      notification.error({ message: '加载模块版本失败', description: await getErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadModuleList(1, page.size);
  }, [refreshKey]);

  const columns: ColumnsType<UpdateModuleVersionRecord> = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    {
      title: '版本',
      dataIndex: 'module_version',
      width: 160,
      render: (value: string, row) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{value}</Typography.Text>
          <Typography.Text type="secondary">{row.release_name || `Release #${row.release_id}`}</Typography.Text>
        </Space>
      ),
    },
    { title: '构建号', dataIndex: 'build_number', width: 150 },
    {
      title: '模块状态',
      dataIndex: 'status',
      width: 110,
      render: (value: UpdateModuleStatus) => renderStatusTag(value, moduleStatusMap),
    },
    {
      title: 'OSS Key',
      dataIndex: 'url',
      ellipsis: true,
      render: (value: string, row) => renderOssKey(value, row.download_url),
    },
    {
      title: '校验串',
      dataIndex: 'sha1',
      width: 260,
      render: (value: string) => <Typography.Text copyable={{ text: value }}>{value}</Typography.Text>,
    },
    {
      title: '操作',
      width: 130,
      fixed: 'right',
      render: (_, row) => (
        <Button
          size="small"
          type="primary"
          icon={<EditOutlined />}
          disabled={!canManageModuleRollout(row)}
          onClick={() => rolloutEditor.openRolloutCreate(row)}
        >
          创建 rollout
        </Button>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Button icon={<ReloadOutlined />} onClick={() => loadModuleList()} loading={loading}>
        刷新
      </Button>
      <Form<ModuleFilterForm>
        form={filterForm}
        layout="inline"
        initialValues={{
          release_statuses: [...defaultReleaseStatusFilter],
          module_statuses: [...defaultModuleStatusFilter],
        }}
        onFinish={() => loadModuleList(1, page.size)}
        style={{ marginBottom: 16 }}
      >
        <Form.Item<ModuleFilterForm> label="模块 Key" name="module_key">
          <Input allowClear placeholder="例如 platform" />
        </Form.Item>
        <Form.Item<ModuleFilterForm> label="Release 状态" name="release_statuses">
          <Select
            mode="multiple"
            allowClear
            maxTagCount="responsive"
            style={{ minWidth: 280 }}
            options={releaseStatusOptions}
            placeholder="选择 Release 状态"
          />
        </Form.Item>
        <Form.Item<ModuleFilterForm> label="模块状态" name="module_statuses">
          <Select
            mode="multiple"
            allowClear
            maxTagCount="responsive"
            style={{ minWidth: 180 }}
            options={moduleStatusOptions}
            placeholder="选择模块状态"
          />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit">
              查询
            </Button>
            <Button
              onClick={() => {
                filterForm.resetFields();
                loadModuleList(1, page.size);
              }}
            >
              重置
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Flex justify="space-between" align="center">
          <Typography.Text type="secondary">
            当前页 {records.length} 条记录，共 {page.total} 条
          </Typography.Text>
          <Space>
            <Button disabled={page.current <= 1 || loading} onClick={() => loadModuleList(page.current - 1, page.size)}>
              上一页
            </Button>
            <Typography.Text>
              第 {page.current} 页 / 每页 {page.size}
            </Typography.Text>
            <Button
              disabled={page.current * page.size >= page.total || loading}
              onClick={() => loadModuleList(page.current + 1, page.size)}
            >
              下一页
            </Button>
          </Space>
        </Flex>

        {grouped.map((group) => (
          <Space key={group.moduleKey} direction="vertical" style={{ width: '100%' }} size={6}>
            <Space>
              <Typography.Text strong>{group.moduleKey}</Typography.Text>
              <Tag>{group.rows.length} 个版本</Tag>
            </Space>
            <Table<UpdateModuleVersionRecord>
              rowKey="id"
              size="small"
              loading={loading}
              columns={columns}
              dataSource={group.rows}
              pagination={false}
              scroll={{ x: 1080 }}
            />
          </Space>
        ))}
      </Space>

      {rolloutEditor.modal}
    </Space>
  );
}
